import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { isNannyUser } from "@/types/user";

type TourStatus = "not_started" | "in_progress" | "completed";

type TourContextType = {
  isTourAvailable: boolean; // Whether the tour should be available (demo or new account)
  tourStatus: TourStatus; // Current status of the tour
  startTour: () => void; // Start the tour
  endTour: () => void; // End the tour
  resetTour: () => void; // Reset the tour to make it available again
};

const TourContext = createContext<TourContextType | undefined>(undefined);

// Tour status is saved in localStorage to persist between sessions
const TOUR_STATUS_KEY = "nannyTax_tour_status";

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [tourStatus, setTourStatus] = useState<TourStatus>("not_started");
  const [isTourAvailable, setIsTourAvailable] = useState(false);

  // Load saved tour status from localStorage
  useEffect(() => {
    // Use a unique key for each user type
    const userType = user && isNannyUser(user) ? 'nanny' : 'employer';
    const statusKey = `${TOUR_STATUS_KEY}_${userType}`;
    const savedStatus = localStorage.getItem(statusKey);
    
    if (savedStatus) {
      setTourStatus(savedStatus as TourStatus);
    } else {
      // Reset to not_started when switching between account types
      setTourStatus("not_started");
    }
  }, [user]);

  // Check if the user qualifies for a tour (demo account or recently joined)
  useEffect(() => {
    if (!user) {
      setIsTourAvailable(false);
      return;
    }

    // Check if user is a demo account
    // This assumes the backend sends this info in user details
    // We would need to update the user type to include isDemoAccount
    const isDemoAccount = (user as any).demo_account === true;

    // Check if account is less than 7 days old
    const dateJoined = (user as any).date_joined;
    const isNewAccount = dateJoined
      ? new Date(dateJoined) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      : false;

    setIsTourAvailable(isDemoAccount || isNewAccount);
  }, [user]);

  useEffect(() => {
    if (isTourAvailable && tourStatus === "not_started") {
      // Auto-start the tour for new users
      startTour();
    }
  }, [isTourAvailable, tourStatus]);

  const startTour = () => {
    setTourStatus("in_progress");
    const userType = user && isNannyUser(user) ? 'nanny' : 'employer';
    localStorage.setItem(`${TOUR_STATUS_KEY}_${userType}`, "in_progress");
  };

  const endTour = () => {
    setTourStatus("completed");
    const userType = user && isNannyUser(user) ? 'nanny' : 'employer';
    localStorage.setItem(`${TOUR_STATUS_KEY}_${userType}`, "completed");
  };

  const resetTour = () => {
    setTourStatus("not_started");
    const userType = user && isNannyUser(user) ? 'nanny' : 'employer';
    localStorage.setItem(`${TOUR_STATUS_KEY}_${userType}`, "not_started");
  };

  return (
    <TourContext.Provider
      value={{
        isTourAvailable,
        tourStatus,
        startTour,
        endTour,
        resetTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
